import { useEffect, useMemo, useRef, useState } from "react";
import { type Dispatch } from "redux";
import { connect, type ConnectedProps } from "react-redux";

import TaskStatuses from "../../../../../enums/taskStatuses";
import uploaderService from "../../services/uploaderService";
import fileUtils from "../../../../../utils/fileUtils";
import {
  addTask,
  deleteTask,
  updateId,
  updateProgress,
  updateTask,
} from "../../../../BackgroundTasks/state/backgroundTasksActions";

import { ValidatedForm } from "../../../../../components/forms";
import { type ScormTask } from "../../../../BackgroundTasks/taskPool";
import { createItemAction } from "../../state/thunks/EntityStateThunk";
import { type RootState } from "../../../../Application/globaltypes/redux";
import { type FileLike } from "../../../../../interfaces";
import { getFilenameWithoutExtension } from "../../../../../utils/stringUtils";
import { bindAction } from "../../../../../interfaces";
import { type FormikProps } from "formik";
import type { EntityMode, FormFields } from "../../types/models";
import { type FormProps } from "./ConfigurationForm";
import { updateFileAction, uploadFileAction } from "../../state/thunks/DetailsThunk";
import { uploadCancel } from "../../state/slices/DetailsSlice";
import { uploadedScorms } from "../../../../../utils/validationSchemas/filesValidationSchema";

interface UploadFieldProps {
  id: number;
  value: FileLike[];
  disabled: boolean;
  firstUpload: boolean;
  onFileUpload: (fileName: string, uploadedFiles: FileLike[], title: string, description: string) => void;
  formik: { [key in keyof FormikProps<FormFields>]: FormProps[key] };
  mode: EntityMode;
  isReadOnlyView: boolean;
  isProcessing: boolean;
  processingError?: string;
}

export type UploadFieldPropsAll = UploadFieldProps & PropsFromRedux;

export const UploadField = (props: UploadFieldPropsAll) => {
  const [taskId, setTaskId] = useState<number>(0);
  const canCancel = props.mode === "create";
  const idProvider = useRef(props.id);
  const task = (props.tasks?.[taskId || props.id] ?? {}) as ScormTask;

  const { fieldErrors, fieldTouched } = useMemo(() => {
    const hasProcessingError = Boolean(props.processingError);
    return {
      fieldErrors: hasProcessingError
        ? { ...props.formik.errors, uploadedFiles: props.processingError }
        : props.formik.errors,
      fieldTouched: hasProcessingError ? { ...props.formik.touched, uploadedFiles: true } : props.formik.touched,
    };
  }, [props.processingError, props.formik.errors, props.formik.touched]);

  useEffect(() => {
    idProvider.current = props.id;
  }, [props.id]);

  const handleFileChange = (files: FileLike[]) => {
    const file = files[0];
    const fileName = getFilenameWithoutExtension(file.name);

    props.onFileUpload(
      file.name,
      [fileUtils.toFileLike(file)],
      props.firstUpload ? fileName : props.formik.values.title,
      props.firstUpload ? fileName : props.formik.values.description,
    );
  };

  const uploaderServiceRef = useRef(
    uploaderService({
      canCancel,
      createAction: props.createFile,
      updateFileAction: props.updateFile,
      uploadAction: props.uploadFile,
      uploadCancelAction: props.uploadFileCancel,
      idProvider,
      onTaskIdChanged: setTaskId,
      tasksActions: {
        addTask: props.addBackgroundTask,
        deleteTask: props.deleteBackgroundTask,
        updateTask: props.updateBackgroundTask,
        updateId: props.updateBackgroundTaskId,
        updateProgress: props.updateBackgroundTaskProgress,
      },
    }),
  );

  return (
    <ValidatedForm.UploadFileField
      {...props.formik}
      id="fileUpload"
      label="File"
      loadingButtonLabel="Loading..."
      accept="application/zip"
      propertyName="uploadedFiles"
      value={props.value}
      disabled={props.disabled || props.isProcessing}
      errors={fieldErrors}
      touched={fieldTouched}
      filesValidator={uploadedScorms}
      onChangeHandler={handleFileChange}
      uploadFilesHandler={uploaderServiceRef.current.uploadFilesHandler}
      uploadingError={TaskStatuses.isFailed(task.status)}
      isUploading={task.isUploading}
      uploadingProgress={task.percent}
      onCancelFileUploading={uploaderServiceRef.current.onCancelFileUploading}
      canCancel
      markAsRequired
      showPercents={false}
      isReadOnlyView={props.isReadOnlyView}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  tasks: state.backgroundTasks.tasks,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => ({
  uploadFile: bindAction(uploadFileAction, dispatch),
  uploadFileCancel: bindAction(uploadCancel, dispatch),
  createFile: bindAction(createItemAction, dispatch),
  updateFile: bindAction(updateFileAction, dispatch),
  addBackgroundTask: bindAction(addTask, dispatch),
  deleteBackgroundTask: bindAction(deleteTask, dispatch),
  updateBackgroundTask: bindAction(updateTask, dispatch),
  updateBackgroundTaskId: bindAction(updateId, dispatch),
  updateBackgroundTaskProgress: bindAction(updateProgress, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UploadField);
export default ConnectedComponent;
